import React from 'react';
import { UserIcon, EditIcon, WalletIcon, StarIcon, PackageIcon } from 'lucide-react';
interface UserProfileCardProps {
  user?: {
    name: string;
    email: string;
    phone: string;
    avatar?: string;
  };
  onSignIn: () => void;
  onRegister: () => void;
  onEdit?: () => void;
  /** Quick stats shown as glassy chips on the gradient header (mobile) */
  stats?: {
    points?: number;
    walletBalance?: string;
    orders?: number;
  };
}
export function UserProfileCard({
  user,
  onSignIn,
  onRegister,
  onEdit,
  stats
}: UserProfileCardProps) {
  // Guest view (unchanged simple layout)
  if (!user) {
    return <div className="bg-white px-4 py-6 border-b border-gray-200" data-id="element-1103" data-mp="2s69j4broi1eu">
        <div className="flex items-center gap-4" data-id="element-1104" data-mp="2o4y0wrb4wigo">
          <div className="w-16 h-16 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0" data-id="element-1105" data-mp="2o33ef8k7siah">
            <UserIcon className="w-8 h-8 text-gray-400" data-id="element-1106" data-mp="xulassfycx3f" />
          </div>
          <div className="flex-1" data-id="element-1107" data-mp="tszropctrmks">
            <h2 className="text-lg font-bold text-gray-900 mb-2" data-id="element-1108" data-mp="2oukuqatmj0yo">
              Hello, Guest
            </h2>
            <div className="flex gap-3" data-id="element-1109" data-mp="2ttea68sh121h">
              <button onClick={onSignIn} className="px-6 py-2 bg-[#1E2B55] text-white text-sm font-semibold rounded-md hover:bg-[#162041] transition-colors" data-id="element-1110" data-mp="2tu7aaqwxctan">
                Sign In
              </button>
              <button onClick={onRegister} className="px-6 py-2 border-2 border-[#1E2B55] text-[#1E2B55] text-sm font-semibold rounded-md hover:bg-gray-50 transition-colors" data-id="element-1111" data-mp="38itzdkccszf2">
                Register
              </button>
            </div>
          </div>
        </div>
      </div>;
  }
  return <div className="relative bg-white" data-id="element-1112" data-mp="3mhtd3wybvtwg">
      {/* Gradient hero (mobile-tuned, app-like) */}
      <div className="relative bg-gradient-to-br from-[#1E2B55] via-[#243567] to-[#2a3d6f] px-4 pt-5 pb-16 overflow-hidden" data-id="element-1113" data-mp="1n6iry5aorbcr">
        {/* Decorative glow */}
        <div className="absolute -top-12 -right-10 w-48 h-48 rounded-full bg-white/5 blur-2xl pointer-events-none" aria-hidden="true" data-id="element-1114" data-mp="1k3cnl02pr4ax" />
        <div className="absolute -bottom-10 -left-6 w-40 h-40 rounded-full bg-amber-400/10 blur-2xl pointer-events-none" aria-hidden="true" data-id="element-1115" data-mp="1g43vs0r8hgi0" />
        <div className="relative flex items-center gap-3" data-id="element-1116" data-mp="1p1xpr51izy20">
          {/* Avatar with gold tier ring */}
          <div className="relative flex-shrink-0" data-id="element-1117" data-mp="2315qi2dc7e8b">
            <div className="absolute inset-0 rounded-full bg-gradient-to-tr from-amber-300 to-amber-500 p-[2px]" data-id="element-1118" data-mp="1z152pr4clqol">
              <div className="w-full h-full rounded-full bg-[#1E2B55]" data-id="element-1119" data-mp="2cstcvov1e52x" />
            </div>
            <div className="relative w-16 h-16 rounded-full bg-gradient-to-br from-[#2a3d6f] to-[#1E2B55] flex items-center justify-center m-[2px]" data-id="element-1120" data-mp="27tgtxf7adi5m">
              {user.avatar ? <img src={user.avatar} alt={user.name} className="w-full h-full rounded-full object-cover" data-id="element-1121" data-mp="2hrd1jx10luyz" /> : <span className="text-2xl font-bold text-white" data-id="element-1122" data-mp="35err0lxuj4b6">
                  {user.name.charAt(0).toUpperCase()}
                </span>}
            </div>
          </div>
          <div className="flex-1 min-w-0" data-id="element-1123" data-mp="2rhcxb50k8ade">
            <h2 className="text-lg font-bold text-white truncate" data-id="element-1124" data-mp="2wdadnbbtpgq6">
              {user.name}
            </h2>
          </div>
          <button onClick={onEdit} aria-label="Edit profile" className="flex-shrink-0 w-10 h-10 rounded-full bg-white/10 backdrop-blur-sm border border-white/15 text-white flex items-center justify-center active:scale-95 hover:bg-white/20 transition" data-id="element-1125" data-mp="2hnmd4cbgum3i">
            <EditIcon className="w-4.5 h-4.5" data-id="element-1126" data-mp="2do3qnc3zy2yk" />
          </button>
        </div>
      </div>
      {/* Floating stats card overlapping gradient */}
      <div className="px-4 -mt-10 relative z-10" data-id="element-1127" data-mp="23pegy6m97ote">
        <div className="bg-white rounded-2xl shadow-[0_4px_20px_rgba(30,43,85,0.12)] border border-gray-100 grid grid-cols-3 divide-x divide-gray-100" data-id="element-1128" data-mp="23oweutcwugdk">
          <StatCell icon={<StarIcon className="w-4 h-4 text-amber-500" data-id="element-1130" data-mp="322a1qzsbap3n" />} label="Points" value={(stats?.points ?? 3250).toLocaleString()} data-id="element-1129" data-mp="321h54rn2hfz8" />
          <StatCell icon={<WalletIcon className="w-4 h-4 text-emerald-600" data-id="element-1132" data-mp="2sc7bh7ckrp90" />} label="Wallet" value={stats?.walletBalance ?? 'SAR 250'} data-id="element-1131" data-mp="2sbfg9xmw3tj9" />
          <StatCell icon={<PackageIcon className="w-4 h-4 text-[#1E2B55]" data-id="element-1134" data-mp="2jazd6pa26oex" />} label="Orders" value={String(stats?.orders ?? 5)} data-id="element-1133" data-mp="2na8a230eb2ep" />
        </div>
      </div>
    </div>;
}
function StatCell({
  icon,
  label,
  value
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return <div className="flex flex-col items-center justify-center py-3 px-2" data-id="element-1135" data-mp="ckt6v662wfz2">
      <div className="flex items-center gap-1 mb-0.5" data-id="element-1136" data-mp="7l6ueyy2r3rb">{icon}</div>
      <span className="text-[11px] text-gray-500 leading-none mt-0.5" data-id="element-1137" data-mp="7lzszcmvqmyd">
        {label}
      </span>
      <span className="text-sm font-bold text-gray-900 mt-1 leading-none" data-id="element-1138" data-mp="f4frpttb3c00">
        {value}
      </span>
    </div>;
}